export const BRAND_COLORS = {
  gray: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    325: "#D1D3DB",
    400: "#9CA3AF",
    450: "#6B7080",
    500: "#6B7280",
    550: "#9CA1AF",
    600: "#4B5163",
    700: "#393F4F",
    750: "#313643",
    800: "#242832",
    850: "#1D2028",
    900: "#111827",
    950: "#090A0C",
  },
  black: {
    500: "#000",
  },
  teal: {
    50: "#F4F9F8",
    100: "#DCEBE9",
    200: "#B9D6D1",
    300: "#8EBAB5",
    400: "#679A96",
    500: "#4D7F7C",
    600: "#406A68",
    700: "#35504F",
    800: "#2F4040",
    900: "#273534",
    950: "#2A3C41",
  },
  cream: {
    50: "#FBF7F4",
    100: "#EBE5E0",
    200: "#ECDDD3",
    300: "#F5F4F4",
  },
  red: {
    50: "#FEE2E2",
    100: "#FECACA",
    500: "#EF4444",
    600: "#DC2626",
    700: "#B91C1C",
    800: "#991B1B",
  },
  sky: {
    100: "#E6EEF9",
    500: "#0EA5E9",
    600: "#5C6AC5",
  },
  emerald: {
    100: "#ECFDF5",
    200: "#DCFCE7",
    400: "#57B67A",
    500: "#10B981",
    700: "#047857",
    900: "#14532D",
  },
  yellow: {
    50: "#FEFCE8",
    100: "#FEF9C3",
    300: "#FDE047",
    500: "#EAB308",
    600: "#CA8A04",
    700: "#A16207",
    900: "#3D2E12",
  },
  purple: {
    500: "#A855F7",
  },
  pink: {
    500: "#EC4899",
    fuchsia: "#D946EF",
  },
  orange: {
    500: "#F97316",
    800: "#A16207",
  },
  invertedPrimary: "#FFF",
  invertedPrimary80: "rgba(224, 224, 224, 0.80)",
  white80: "#ffffffcc",
  grayOpacity70: "rgba(9, 10, 12, 0.7)",
};

export const BASE_COLORS = {
  background: {
    primary: BRAND_COLORS.invertedPrimary,
    primaryDark: BRAND_COLORS.gray[850],
    secondary: BRAND_COLORS.gray[50],
    secondaryDark: BRAND_COLORS.gray[750],
    tertiary: BRAND_COLORS.gray[100],
    alternative: BRAND_COLORS.gray[200],
    brand: BRAND_COLORS.teal[600],
    brandLight: BRAND_COLORS.teal[50],
    brandDark: BRAND_COLORS.gray[700],
    inverted: BRAND_COLORS.gray[950],
    scheduleToday: BRAND_COLORS.red[500],
    searchTextHighlight: BRAND_COLORS.yellow[300],
    scoutPressed: BRAND_COLORS.teal[700],
    linearGradientScout: "linear-gradient(180deg , #4D7F7C 0%, #4D7F7C 100%, #4D7F7C)",
    linearGradientScoutHover: "linear-gradient(180deg , #406A68 0%, #406A68 100%, #406A68)",
    mainButton:
      "linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%), #4D7F7C",
    success: BRAND_COLORS.emerald[500],
    successLight: BRAND_COLORS.emerald[200],
    warningLight: BRAND_COLORS.yellow[50],
    errorLight: BRAND_COLORS.red[50],
    tag: "#F0F1F4",
    tagMemberDefault: BRAND_COLORS.teal[100],
    currentChecklist: BRAND_COLORS.teal[100],
    onboarding: BRAND_COLORS.cream[300],
    onboardingOverlay: "rgba(0, 0, 0, 0.40)",
    clear: "#F9F9FB",
    blueLight: "#E0F2FE",
  },
  sidebar: {
    primary: BRAND_COLORS.cream[50],
    secondary: BRAND_COLORS.cream[100],
    tertiary: BRAND_COLORS.cream[200],
  },
  stroke: {
    primary: BRAND_COLORS.gray[200],
    primaryDark: BRAND_COLORS.gray[750],
    secondary: BRAND_COLORS.gray[300],
    secondaryDark: BRAND_COLORS.gray[300],
    disabled: BRAND_COLORS.gray[100],
    actionPrimary: BRAND_COLORS.teal[600],
    actionPrimaryDark: BRAND_COLORS.teal[400],
    successDark: BRAND_COLORS.emerald[900],
  },
  text: {
    primary: BRAND_COLORS.gray[950],
    primaryDark: BRAND_COLORS.gray[100],
    secondary: BRAND_COLORS.gray[500],
    dark: BRAND_COLORS.black[500],
    disabled: BRAND_COLORS.gray[300],
    warning: BRAND_COLORS.orange[800],
    blue: "#0369A1",
  },
  action: {
    primary: BRAND_COLORS.teal[500],
    primaryHover: BRAND_COLORS.teal[600],
    primaryPressed: BRAND_COLORS.teal[700],
    secondary: BRAND_COLORS.gray[100],
    secondaryHover: BRAND_COLORS.gray[200],
    secondaryPressed: BRAND_COLORS.gray[300],
    danger: BRAND_COLORS.red[600],
    dangerHover: BRAND_COLORS.red[700],
    dangerPressed: BRAND_COLORS.red[800],
    disabled: BRAND_COLORS.gray[300],
  },
  content: {
    primary: BRAND_COLORS.gray[950],
    primaryOpacity70: BRAND_COLORS.grayOpacity70,
    secondary: BRAND_COLORS.gray[500],
    disabled: BRAND_COLORS.gray[400],
    text: BRAND_COLORS.invertedPrimary,
    white80: BRAND_COLORS.white80,
    textOpacity80: BRAND_COLORS.invertedPrimary80,
    link: BRAND_COLORS.teal[600],
    success: BRAND_COLORS.emerald[700],
    successLight: BRAND_COLORS.emerald[100],
    error: BRAND_COLORS.red[700],
    errorLight: BRAND_COLORS.red[100],
    warning: BRAND_COLORS.orange[800],
    warningLight: BRAND_COLORS.yellow[100],
  },
  meetingType: {
    pipelineReview: BRAND_COLORS.emerald[500],
    prospectMeeting: BRAND_COLORS.sky[500],
    investmentCommittee: BRAND_COLORS.yellow[500],
    boardMeeting: BRAND_COLORS.purple[500],
    diligenceMeeting: BRAND_COLORS.pink[500],
    fundraising: BRAND_COLORS.orange[500],
    genericMeeting: BRAND_COLORS.red[500],
  },
  icon: {
    primary: BRAND_COLORS.gray[950],
    secondary: BRAND_COLORS.gray[500],
    disabled: BRAND_COLORS.gray[300],
    red: BRAND_COLORS.red[700],
  },
};

export const APP_COLORS = { ...BRAND_COLORS, ...BASE_COLORS };
